import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useRef, lazy, Suspense } from 'react'
import { ModalBoxOverlay } from '../../Common/ModalBox/ModalBoxOverlay'
import MeetingCancelIcon from 'components/Common/SvgIcons/MeetingCancelIcon'
// import SolidTrashIcon from '../../Common/SvgIcons/SolidTrashIcon'
// import PencilIcon from '../../Common/SvgIcons/PencilIcon'
import { Trash2, Pencil } from 'lucide-react'
import { CALENDAR, CalendarTailwindColorsMap, calendarLabelMap } from '../../../config'
import styled from 'styled-components'
import tw from 'twin.macro'
import { extendedCalendarEvents } from 'types'
import colors from 'colors'

// Lazy load components outside the component to prevent re-declaration on every render
const EditMeetingContent = lazy(() =>
  import('./EditMeetingContent').then((mod) => ({ default: mod.EditMeetingContent }))
)
const ViewMeetingContent = lazy(() =>
  import('./ViewMeetingContent').then((mod) => ({ default: mod.ViewMeetingContent }))
)

interface IEditMeetingModalProps {
  onClose?: (e) => void
  id?: string
  title?: string
  onEdit: (e) => void
  onCancel: (e) => void
  editMode: boolean
  eventType: CALENDAR
  event: extendedCalendarEvents
  userTimezone: string
  currentUserId?: string
  onSave: (data: {
    title: string
    startDateTime: Date
    endDateTime: Date
    attendees: string[]
    teamMembers: string[]
    description: string
    location: string
    meetingLink: string
    timezone: string
  }) => void
  isDisabled?: boolean
}

export const EditMeetingModalHeader = ({
  onClose,
  onEdit,
  onCancel,
  title,
  editMode,
  eventType,
}: {
  onClose?: (e: any) => void
  onEdit?: (e: any) => void
  onCancel?: (e: any) => void
  editMode: boolean
  title?: string
  eventType?: CALENDAR
}): JSX.Element => (
  <div className="flex flex-wrap flex-col w-full">
    <div className={`flex flex-row items-center px-4 py-3 top-0 w-full`}>
      {eventType === CALENDAR.INTERNAL && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-md transition-colors ${
              editMode
                ? 'text-primary-disabledText cursor-not-allowed'
                : 'text-primary-text hover:bg-gray-100'
            }`}
            onClick={onEdit}
            disabled={editMode}
          >
            <Pencil
              className="w-4 h-4"
              color={editMode ? '#A0AEC0' : colors.primary.disabledTextLight}
            />
            <span className={editMode ? 'text-primary-disabledText' : 'text-primary-text'}>
              Edit
            </span>
          </button>
          <button
            type="button"
            className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-red-500 hover:bg-red-50 rounded-md transition-colors"
            onClick={onCancel}
          >
            <Trash2 className="w-4 h-4" color={colors.primary.error} />
            <span className="text-primary-error">Delete</span>
          </button>
        </div>
      )}

      {onClose && (
        <div onClick={onClose} className="cursor-pointer ml-auto">
          <MeetingCancelIcon className={'text-primary-text h-[20px] w-[20px]'} />
        </div>
      )}
    </div>

    {/* Horizontal divider below Edit/Delete buttons */}
    <div className="w-full border-b border-grey-lightest"></div>

    {eventType !== CALENDAR.INTERNAL && (
      <div className="w-full ml-6 flex items-start mt-3 mb-3">
        <div
          className={`my-auto w-3 h-3 rounded-full bg-${CalendarTailwindColorsMap.get(
            eventType ?? CALENDAR.INTERNAL
          )}`}
        ></div>
        <p className="ml-2 text-primary-text">
          {calendarLabelMap[eventType ?? CALENDAR.INTERNAL]} Event
        </p>
      </div>
    )}

    {title && (
      <div className={`w-full flex mt-6 mb-2 ${editMode ? 'px-6' : 'px-10'}`}>
        <p className="text-xl md:text-2xl font-bold break-words overflow-wrap-anywhere pr-4">
          {title}
        </p>
      </div>
    )}
  </div>
)

const ModalWrapper = styled.div<{ isEditMode: boolean }>`
  ${tw`bg-white shadow-xl flex flex-col mx-auto my-auto rounded-xl`};
  ${({ isEditMode }) =>
    isEditMode
      ? tw`h-[90vh] w-[80vw] max-w-[785px] overflow-hidden`
      : tw`w-[90vw] max-w-[650px] max-h-[90vh] overflow-y-auto`};
`

export const EditMeetingModal = ({
  onClose,
  onEdit,
  onCancel,
  id,
  title,
  editMode,
  eventType,
  event,
  userTimezone,
  currentUserId,
  onSave,
  isDisabled = false,
}: IEditMeetingModalProps) => {
  const currentRef = useRef(null)

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog
        initialFocus={currentRef}
        id={id}
        as="div"
        className={`relative z-50`}
        onClose={() => undefined}
      >
        <ModalBoxOverlay bgColor="bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <ModalWrapper ref={currentRef} isEditMode={editMode}>
            <EditMeetingModalHeader
              onClose={onClose}
              onEdit={onEdit}
              title={title}
              editMode={editMode}
              onCancel={onCancel}
              eventType={eventType}
            />
            {editMode ? (
              <div className="flex-1 overflow-hidden flex flex-col">
                <Suspense
                  fallback={
                    <div className="px-6 py-8 text-center text-primary-text">Loading...</div>
                  }
                >
                  <EditMeetingContent
                    event={event}
                    onSave={onSave}
                    onCancel={() => onClose && onClose(null)}
                    isDisabled={isDisabled}
                  />
                </Suspense>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <Suspense
                  fallback={
                    <div className="px-6 py-8 text-center text-primary-text">Loading...</div>
                  }
                >
                  <ViewMeetingContent
                    event={event}
                    userTimezone={userTimezone}
                    currentUserId={currentUserId}
                  />
                </Suspense>
              </div>
            )}
          </ModalWrapper>
        </div>
      </Dialog>
    </Transition>
  )
}
